import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  X, Calendar, Users, Award, BookOpen, User, Clock, AlertTriangle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ViewAsUser = ({ userId, userName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('enrollments');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/admin/users/${userId}/profile`);
        setProfile(response.data);
      } catch (error) {
        toast.error('Errore nel caricamento del profilo utente');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  const formatDateRange = (startDate, endDate, dateTbd) => {
    if (dateTbd) return 'DATA DA DEFINIRE';
    if (!startDate) return 'Data non disponibile';
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    const start = new Date(startDate).toLocaleDateString('it-IT', options);
    if (!endDate || startDate === endDate) return start;
    const end = new Date(endDate).toLocaleDateString('it-IT', options);
    return `${start} - ${end}`;
  };

  const getEnrollmentStatusBadge = (enrollment) => {
    if (enrollment.status === 'approved' && enrollment.paid !== false) {
      return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">ISCRITTO - PAGATO</span>;
    }
    if (enrollment.status === 'approved' && enrollment.paid === false) {
      return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">ISCRITTO - Pagamento da effettuare</span>;
    }
    if (enrollment.status === 'pending_payment') {
      return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">ISCRITTO - Pagamento da effettuare</span>;
    }
    if (enrollment.status === 'pending') {
      return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">In Attesa di Approvazione</span>;
    }
    if (enrollment.status === 'rejected') {
      return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">Rifiutata</span>;
    }
    return <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">{enrollment.status}</span>;
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      teacher: 'bg-blue-100 text-blue-800',
      formatore: 'bg-cyan-100 text-cyan-800',
      maestro: 'bg-purple-100 text-purple-800',
      atleta: 'bg-green-100 text-green-800',
      contabile: 'bg-amber-100 text-amber-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="view-as-user-loading">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento vista utente...</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { user, events, enrollments = [], match_analyses = [], detailed_analyses = [], certificates = [] } = profile;
  const roles = user.roles || [user.role || 'atleta'];
  const isAthlete = roles.includes('atleta');

  const mandatoryFields = [
    { key: 'birth_date', label: 'Data di Nascita' },
    { key: 'fiscal_code', label: 'Codice Fiscale' },
    { key: 'athlete_class', label: 'Classe' },
    { key: 'asa_reference', label: 'ASA' },
    { key: 'phone', label: 'Telefono' },
    { key: 'address', label: 'Indirizzo' },
    { key: 'region', label: 'Regione' },
    { key: 'disciplines', label: 'Discipline' },
  ];

  const filledFields = isAthlete ? mandatoryFields.filter(f => {
    if (f.key === 'disciplines') {
      return (user.disciplines && user.disciplines.length > 0) || (user.disciplines_other && user.disciplines_other.trim());
    }
    return user[f.key];
  }) : [];
  const missingFields = isAthlete ? mandatoryFields.filter(f => {
    if (f.key === 'disciplines') {
      return !(user.disciplines && user.disciplines.length > 0) && !(user.disciplines_other && user.disciplines_other.trim());
    }
    return !user[f.key];
  }) : [];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="view-as-user-modal">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header banner */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" />
            <div>
              <p className="text-sm opacity-80">Vista come utente</p>
              <h2 className="text-lg font-bold">{user.name} {user.surname}</h2>
            </div>
            <div className="flex gap-1 ml-2">
              {roles.map(role => (
                <Badge key={role} className="bg-white/20 text-white border-white/30 uppercase text-xs">
                  {role}
                </Badge>
              ))}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20" data-testid="close-view-as-user">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* User Info Card */}
          <Card className="mb-6 border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                  <p className="font-medium" data-testid="view-as-email">{user.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Stato Account</p>
                  <Badge className={user.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {user.approved ? 'Approvato' : 'In Attesa'}
                  </Badge>
                </div>
                {user.athlete_number && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Numero Atleta</p>
                    <p className="font-mono font-medium">{user.athlete_number}</p>
                  </div>
                )}
              </div>

              {/* Athlete mandatory fields status */}
              {isAthlete && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Profilo Atleta ({filledFields.length}/{mandatoryFields.length} campi compilati)</p>
                  <div className="flex flex-wrap gap-2">
                    {mandatoryFields.map(f => {
                      const isFilled = f.key === 'disciplines'
                        ? (user.disciplines && user.disciplines.length > 0) || (user.disciplines_other && user.disciplines_other.trim())
                        : !!user[f.key];
                      const displayValue = f.key === 'disciplines'
                        ? [...(user.disciplines || []), user.disciplines_other].filter(Boolean).join(', ')
                        : user[f.key];
                      return (
                      <Badge
                        key={f.key}
                        variant="outline"
                        className={isFilled ? 'border-green-300 text-green-700 bg-green-50' : 'border-red-300 text-red-700 bg-red-50'}
                        data-testid={`field-status-${f.key}`}
                      >
                        {isFilled ? '\u2713' : '\u2717'} {f.label}{isFilled && displayValue ? `: ${displayValue}` : ''}
                      </Badge>
                      );
                    })}
                  </div>
                  {missingFields.length > 0 && (
                    <div className="mt-2 p-2 bg-red-50 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <p className="text-sm text-red-700">
                        Questo utente vedrà il modale obbligatorio di completamento profilo al login
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dashboard Tabs - what the user sees */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full flex flex-wrap gap-1 mb-4" data-testid="view-as-tabs">
              <TabsTrigger value="enrollments">Iscrizioni ({enrollments.length})</TabsTrigger>
              <TabsTrigger value="events">Eventi ({events.length})</TabsTrigger>
              {isAthlete && <TabsTrigger value="analyses">Match Analysis ({match_analyses.length})</TabsTrigger>}
              <TabsTrigger value="certificates">Certificati ({certificates.length})</TabsTrigger>
            </TabsList>

            {/* Enrollments Tab */}
            <TabsContent value="enrollments">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="w-4 h-4" />
                    Le Mie Iscrizioni agli Eventi
                  </CardTitle>
                  <CardDescription>Questo è ciò che l'utente vede nella tab "Le Mie Iscrizioni"</CardDescription>
                </CardHeader>
                <CardContent>
                  {enrollments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Non ha iscrizioni</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="view-as-enrollments">
                      {enrollments.map((enrollment) => (
                        <div key={enrollment.id} className="border rounded-lg p-4 bg-white" data-testid={`view-enrollment-${enrollment.id}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <h4 className="font-semibold">{enrollment.event_title}</h4>
                              <p className="text-sm text-gray-500">
                                Iscritto il: {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT', {
                                  day: '2-digit', month: '2-digit', year: 'numeric'
                                })}
                              </p>
                            </div>
                            {getEnrollmentStatusBadge(enrollment)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="w-4 h-4" />
                    I Miei Eventi
                  </CardTitle>
                  <CardDescription>Eventi con iscrizione approvata</CardDescription>
                </CardHeader>
                <CardContent>
                  {events.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Nessun evento</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="view-as-events">
                      {events.map((event) => (
                        <div key={event.id} className="border rounded-lg p-4 bg-white">
                          <h4 className="font-semibold">{event.title}</h4>
                          <p className="text-sm text-gray-600 mb-1">{event.description}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                            <Badge variant="secondary" className="text-xs">{event.type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Match Analysis Tab */}
            {isAthlete && (
              <TabsContent value="analyses">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="w-4 h-4" />
                      Match Analysis
                    </CardTitle>
                    <CardDescription>Analisi ricevute dall'atleta</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {match_analyses.length === 0 && detailed_analyses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Nessuna analisi</p>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="view-as-analyses">
                        {match_analyses.map((analysis) => (
                          <div key={analysis.id} className="border rounded-lg p-4 bg-white">
                            <h4 className="font-semibold">{analysis.event_title}</h4>
                            <p className="text-sm text-gray-600">Insegnante: {analysis.teacher_name}</p>
                            {analysis.result_score != null && (
                              <div className="flex items-center gap-3 my-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <span className="text-sm font-semibold text-purple-700">Punteggio Totale:</span>
                                <span className="text-2xl font-bold text-purple-800">{analysis.result_score}</span>
                              </div>
                            )}
                            <div className="bg-gray-50 rounded p-2 mt-2">
                              <p className="text-xs font-semibold text-gray-500 mb-1">Descrizione Risultato</p>
                              <p className="text-sm mt-1 whitespace-pre-wrap">{analysis.content}</p>
                            </div>
                            {analysis.judgments_advice && (
                              <div className="bg-blue-50 rounded p-2 mt-2 border border-blue-100">
                                <p className="text-xs font-semibold text-blue-600 mb-1">Giudizi e Consigli</p>
                                <p className="text-sm text-blue-800 whitespace-pre-wrap">{analysis.judgments_advice}</p>
                              </div>
                            )}
                            {analysis.has_certificate && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const resp = await axios.get(
                                        `${API}/match-analysis/${analysis.id}/certificate`,
                                        { responseType: 'blob' }
                                      );
                                      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.target = '_blank';
                                      a.rel = 'noopener noreferrer';
                                      a.download = analysis.certificate_pdf_filename || `certificato_${analysis.id}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      a.remove();
                                      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                                    } catch (err) {
                                      console.error('Errore download certificato', err);
                                    }
                                  }}
                                  className="inline-flex items-center gap-2 px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs"
                                  data-testid={`view-as-analysis-certificate-${analysis.id}`}
                                >
                                  📎 Scarica Certificato{analysis.certificate_pdf_filename ? ` (${analysis.certificate_pdf_filename})` : ''}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {detailed_analyses.map((analysis) => (
                          <div key={analysis.id} className="border rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold">{analysis.event_title}</h4>
                              <Badge className="bg-blue-100 text-blue-800">Punteggio: {analysis.total_score}</Badge>
                            </div>
                            <p className="text-sm text-gray-600">Insegnante: {analysis.teacher_name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Certificates Tab */}
            <TabsContent value="certificates">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Award className="w-4 h-4" />
                    Certificati
                  </CardTitle>
                  <CardDescription>Attestati ricevuti dall'utente</CardDescription>
                </CardHeader>
                <CardContent>
                  {certificates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Nessun certificato</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="view-as-certificates">
                      {certificates.map((cert) => (
                        <div key={cert.id} className="border rounded-lg p-4 bg-white">
                          <h4 className="font-semibold">{cert.title}</h4>
                          {cert.description && <p className="text-sm text-gray-600">{cert.description}</p>}
                          <p className="text-xs text-gray-500 mt-1">
                            Emesso il: {new Date(cert.issued_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ViewAsUser;
